import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface IdentificationType {
  id: string;
  name: string;
  active: boolean;
}

interface TenantCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TenantFormData {
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string;
  addressNumber: string;
  addressStreet: string;
  addressNeighborhood: string;
  addressMunicipality: string;
  addressCity: string;
  identificationTypeId: string;
  username: string;
  password: string;
  files: File[];
}

const TenantCreateDialog: React.FC<TenantCreateDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<TenantFormData>({
    fullName: '',
    phoneNumbers: [''],
    emails: [''],
    nationality: '',
    addressNumber: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMunicipality: '',
    addressCity: '',
    identificationTypeId: '',
    username: '',
    password: '',
    files: []
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TenantFormData | 'phoneNumbers' | 'emails', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [identificationTypes, setIdentificationTypes] = useState<IdentificationType[]>([]);
  const [loadingIdentificationTypes, setLoadingIdentificationTypes] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const dispatch = useAppDispatch();

  // Fetch identification types when dialog opens
  useEffect(() => {
    if (open) {
      fetchIdentificationTypes();
      // Reset form
      setFormData({
        fullName: '',
        phoneNumbers: [''],
        emails: [''],
        nationality: '',
        addressNumber: '',
        addressStreet: '',
        addressNeighborhood: '',
        addressMunicipality: '',
        addressCity: '',
        identificationTypeId: '',
        username: '',
        password: '',
        files: []
      });
      setErrors({});
      setNewPhone('');
      setNewEmail('');
    }
  }, [open]);

  const fetchIdentificationTypes = async () => {
    setLoadingIdentificationTypes(true);
    try {
      const response = await DataService.get('api/identification-types/all');
      if (response.ok) {
        const responseData = await response.json();
        let rawTypes: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawTypes = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawTypes = responseData;
        }

        const activeTypes = rawTypes
          .filter(type => type.active)
          .map(type => ({
            id: type.id,
            name: type.name,
            active: type.active
          }));

        setIdentificationTypes(activeTypes);
      }
    } catch (error) {
      console.error('Error fetching identification types:', error);
      dispatch(showAlert({
        message: 'Failed to load identification types',
        type: 'error'
      }));
    } finally {
      setLoadingIdentificationTypes(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof TenantFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle phone number operations
  const addPhoneNumber = () => {
    if (newPhone.trim()) {
      setFormData(prev => ({
        ...prev,
        phoneNumbers: [...prev.phoneNumbers.filter(p => p.trim()), newPhone.trim()]
      }));
      setNewPhone('');
      if (errors.phoneNumbers) {
        setErrors(prev => ({ ...prev, phoneNumbers: undefined }));
      }
    }
  };

  const removePhoneNumber = (index: number) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index)
    }));
  };

  const updatePhoneNumber = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) => i === index ? value : phone)
    }));
  };

  // Handle email operations
  const addEmail = () => {
    if (newEmail.trim() && isValidEmail(newEmail.trim())) {
      setFormData(prev => ({
        ...prev,
        emails: [...prev.emails.filter(e => e.trim()), newEmail.trim()]
      }));
      setNewEmail('');
      if (errors.emails) {
        setErrors(prev => ({ ...prev, emails: undefined }));
      }
    }
  };

  const removeEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const updateEmail = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => i === index ? value : email)
    }));
  };

  // Handle file operations
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof TenantFormData | 'phoneNumbers' | 'emails', string>> = {};

    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    // Phone numbers validation (at least one required)
    const validPhones = formData.phoneNumbers.filter(phone => phone.trim());
    if (validPhones.length === 0) {
      newErrors.phoneNumbers = 'At least one phone number is required';
    }

    // Emails validation (at least one required and valid)
    const validEmails = formData.emails.filter(email => email.trim() && isValidEmail(email.trim()));
    if (validEmails.length === 0) {
      newErrors.emails = 'At least one valid email is required';
    }

    // Nationality validation
    if (!formData.nationality.trim()) {
      newErrors.nationality = 'Nationality is required';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.trim().length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Identification type validation
    if (!formData.identificationTypeId) {
      newErrors.identificationTypeId = 'Identification type is required';
    }

    // Address validation - all or nothing
    const addressFields = [
      formData.addressNumber.trim(),
      formData.addressStreet.trim(),
      formData.addressNeighborhood.trim(),
      formData.addressMunicipality.trim(),
      formData.addressCity.trim()
    ];
    const filledAddressFields = addressFields.filter(field => field !== '');

    if (filledAddressFields.length > 0 && filledAddressFields.length < addressFields.length) {
      // At least one address field is filled but not all
      if (!formData.addressNumber.trim()) newErrors.addressNumber = 'Required when any address field is filled';
      if (!formData.addressStreet.trim()) newErrors.addressStreet = 'Required when any address field is filled';
      if (!formData.addressNeighborhood.trim()) newErrors.addressNeighborhood = 'Required when any address field is filled';
      if (!formData.addressMunicipality.trim()) newErrors.addressMunicipality = 'Required when any address field is filled';
      if (!formData.addressCity.trim()) newErrors.addressCity = 'Required when any address field is filled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Create FormData for multipart/form-data
      const submitFormData = new FormData();

      submitFormData.append('FullName', formData.fullName.trim());
      submitFormData.append('Nationality', formData.nationality.trim());
      submitFormData.append('IdentificationTypeId', formData.identificationTypeId);
      submitFormData.append('Username', formData.username.trim());
      submitFormData.append('Password', formData.password.trim());

      // Add phone numbers
      const validPhones = formData.phoneNumbers.filter(phone => phone.trim());
      validPhones.forEach(phone => {
        submitFormData.append('PhoneNumbers', phone.trim());
      });

      // Add emails
      const validEmails = formData.emails.filter(email => email.trim() && isValidEmail(email.trim()));
      validEmails.forEach(email => {
        submitFormData.append('Emails', email.trim());
      });

      // Add address fields (optional)
      if (formData.addressNumber.trim()) {
        submitFormData.append('AddressNumber', formData.addressNumber.trim());
      }
      if (formData.addressStreet.trim()) {
        submitFormData.append('AddressStreet', formData.addressStreet.trim());
      }
      if (formData.addressNeighborhood.trim()) {
        submitFormData.append('AddressNeighborhood', formData.addressNeighborhood.trim());
      }
      if (formData.addressMunicipality.trim()) {
        submitFormData.append('AddressMunicipality', formData.addressMunicipality.trim());
      }
      if (formData.addressCity.trim()) {
        submitFormData.append('AddressCity', formData.addressCity.trim());
      }

      // Add files (optional)
      formData.files.forEach(file => {
        submitFormData.append('Files', file);
      });

      // Debug: Log what we're sending
      console.log('Submitting tenant data:');
      for (let [key, value] of submitFormData.entries()) {
        console.log(key, value);
      }

      const response = await DataService.postForm('api/Tenant/create', submitFormData);

      if (response.ok) {
        onSuccess();
        dispatch(showAlert({
          message: 'Tenant created successfully!',
          type: 'success'
        }));
      } else {
        // Try to get error details from response
        let errorMessage = 'Failed to create tenant';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to create tenant: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create tenant. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Add New Tenant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the tenant information below. At least one phone number and one email are required.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                error={!!errors.fullName}
                helperText={errors.fullName}
                disabled={submitting}
                fullWidth
                required
                placeholder="Enter full name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Nationality"
                value={formData.nationality}
                onChange={handleInputChange('nationality')}
                error={!!errors.nationality}
                helperText={errors.nationality}
                disabled={submitting}
                fullWidth
                required
                placeholder="Enter nationality"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.identificationTypeId} disabled={submitting || loadingIdentificationTypes}>
                <InputLabel>Identification Type *</InputLabel>
                <Select
                  value={formData.identificationTypeId}
                  onChange={handleInputChange('identificationTypeId')}
                  label="Identification Type *"
                >
                  {identificationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.identificationTypeId && (
                  <FormHelperText>{errors.identificationTypeId}</FormHelperText>
                )}
                {loadingIdentificationTypes && (
                  <FormHelperText>Loading identification types...</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                error={!!errors.username}
                helperText={errors.username}
                disabled={submitting}
                fullWidth
                required
                placeholder="Enter username"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!errors.password}
                helperText={errors.password}
                disabled={submitting}
                fullWidth
                required
                placeholder="Enter password"
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address Information (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Address Number"
                value={formData.addressNumber}
                onChange={handleInputChange('addressNumber')}
                error={!!errors.addressNumber}
                helperText={errors.addressNumber}
                disabled={submitting}
                fullWidth
                placeholder="Building/House number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Street"
                value={formData.addressStreet}
                onChange={handleInputChange('addressStreet')}
                error={!!errors.addressStreet}
                helperText={errors.addressStreet}
                disabled={submitting}
                fullWidth
                placeholder="Street name"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Neighborhood"
                value={formData.addressNeighborhood}
                onChange={handleInputChange('addressNeighborhood')}
                error={!!errors.addressNeighborhood}
                helperText={errors.addressNeighborhood}
                disabled={submitting}
                fullWidth
                placeholder="Neighborhood"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Municipality"
                value={formData.addressMunicipality}
                onChange={handleInputChange('addressMunicipality')}
                error={!!errors.addressMunicipality}
                helperText={errors.addressMunicipality}
                disabled={submitting}
                fullWidth
                placeholder="Municipality"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="City"
                value={formData.addressCity}
                onChange={handleInputChange('addressCity')}
                error={!!errors.addressCity}
                helperText={errors.addressCity}
                disabled={submitting}
                fullWidth
                placeholder="City"
              />
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Phone Numbers *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="Add Phone Number"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  disabled={submitting}
                  placeholder="Enter phone number"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhoneNumber())}
                />
                <Button
                  variant="outlined"
                  onClick={addPhoneNumber}
                  disabled={submitting || !newPhone.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {formData.phoneNumbers.filter(p => p.trim()).length > 0 ? (
                <List dense>
                  {formData.phoneNumbers.map((phone, index) =>
                    phone.trim() ? (
                      <ListItem key={index} divider>
                        <TextField
                          value={phone}
                          onChange={(e) => updatePhoneNumber(index, e.target.value)}
                          disabled={submitting}
                          size="small"
                          fullWidth
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removePhoneNumber(index)}
                            disabled={submitting}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ) : null
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No phone numbers added
                </Typography>
              )}
              {errors.phoneNumbers && (
                <Typography variant="caption" color="error">
                  {errors.phoneNumbers}
                </Typography>
              )}
            </Grid>

            {/* Emails */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Email Addresses *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="Add Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={submitting}
                  placeholder="Enter email address"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                />
                <Button
                  variant="outlined"
                  onClick={addEmail}
                  disabled={submitting || !newEmail.trim() || !isValidEmail(newEmail.trim())}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {formData.emails.filter(e => e.trim()).length > 0 ? (
                <List dense>
                  {formData.emails.map((email, index) =>
                    email.trim() ? (
                      <ListItem key={index} divider>
                        <TextField
                          type="email"
                          value={email}
                          onChange={(e) => updateEmail(index, e.target.value)}
                          disabled={submitting}
                          size="small"
                          fullWidth
                          error={email.trim() && !isValidEmail(email.trim())}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removeEmail(index)}
                            disabled={submitting}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ) : null
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No email addresses added
                </Typography>
              )}
              {errors.emails && (
                <Typography variant="caption" color="error">
                  {errors.emails}
                </Typography>
              )}
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Documents (Optional)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="file-upload"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                  disabled={submitting}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={submitting}
                  >
                    Upload Files
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Supported formats: Images, PDF, Word documents
                </Typography>
              </Paper>

              {formData.files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Files:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.files.map((file, index) => (
                      <Chip
                        key={index}
                        icon={<AttachFileIcon />}
                        label={file.name}
                        onDelete={() => removeFile(index)}
                        disabled={submitting}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting ? 'Creating...' : 'Create Tenant'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TenantCreateDialog;